import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { safeNext, withNext } from '../utils/safeNext';
import { useAuth } from '../hooks/useAuth';
import { DWILogoIcon } from '../components/DWILogo';
import { useCountry } from '../contexts/CountryContext';
import CountrySelector from '../components/CountrySelector';
import { User, Mail, Phone, Lock, Eye, EyeOff, Gift, Globe } from 'lucide-react';
import { t, LANGS } from '../i18n/dwisend';

// ── Module-scope constants and components ──────────────────────────────────
// These MUST live outside Register() — inner component definitions cause React
// to see a new type on every re-render, unmounting inputs and losing focus.

const inputBase = {
  width: '100%', paddingTop: '13px', paddingBottom: '13px',
  paddingRight: '14px', paddingLeft: '44px', borderRadius: '12px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.10)',
  color: '#F0F6FF', fontSize: '14px', outline: 'none',
  transition: 'border 0.15s, box-shadow 0.15s', boxSizing: 'border-box',
};

const pageWrap = {
  minHeight: '100vh', background: '#0A1628',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '20px 16px', position: 'relative', overflow: 'hidden',
  fontFamily: 'Inter, system-ui, sans-serif', boxSizing: 'border-box',
};
const cardOuter = {
  background: 'rgba(10,16,30,0.90)',
  border: '1px solid rgba(139,92,246,0.18)',
  borderRadius: '20px', overflow: 'hidden',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset,0 24px 80px rgba(0,0,0,0.55)',
};
const topBar = { height: '2px', background: 'linear-gradient(90deg,transparent 0%,#1B61AC 40%,#00A8DB 70%,transparent 100%)' };

function onFocus(e) {
  e.target.style.border    = '1px solid rgba(6,200,232,0.6)';
  e.target.style.boxShadow = '0 0 0 4px rgba(6,200,232,0.09)';
}
function onBlur(e) {
  e.target.style.border    = '1px solid rgba(255,255,255,0.10)';
  e.target.style.boxShadow = 'none';
}

function Field({ icon: Icon, label, type = 'text', placeholder, value, onChange, note, rightEl }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: '11px', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.13em',
        color: 'rgba(232,239,248,0.4)', marginBottom: '7px',
      }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <Icon size={15} style={{
          position: 'absolute', left: '14px', top: '50%',
          transform: 'translateY(-50%)', color: 'rgba(232,239,248,0.3)', pointerEvents: 'none',
        }} />
        <input
          type={type} placeholder={placeholder} value={value} onChange={onChange}
          required style={{ ...inputBase, paddingRight: rightEl ? '48px' : '14px' }}
          onFocus={onFocus} onBlur={onBlur}
        />
        {rightEl && (
          <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}>
            {rightEl}
          </div>
        )}
      </div>
      {note && <p style={{ fontSize: '11px', color: 'rgba(232,239,248,0.3)', marginTop: '5px' }}>{note}</p>}
    </div>
  );
}

// English / Français (DwiSend Regional): French for DRC sign-ups — from an
// invite link with ?lang=fr, or automatically when DRC is picked. The toggle
// shows only then: a Uganda sign-up without ?lang= looks exactly as before.
function LangToggle({ lang, onChange }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '12px' }}>
      {LANGS.map(l => (
        <button key={l} type="button" onClick={() => onChange(l)}
          style={{ background: lang === l ? 'rgba(0,168,219,0.18)' : 'none', border: 'none', borderRadius: '8px', cursor: 'pointer',
            color: lang === l ? '#00A8DB' : 'rgba(232,239,248,0.4)', fontSize: '12px', fontWeight: 700, padding: '4px 10px' }}>
          {l === 'fr' ? 'Français' : 'English'}
        </button>
      ))}
    </div>
  );
}

function Brand({ lang }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
      <div style={{ position: 'relative', display: 'inline-block', marginBottom: '14px' }}>
        <div style={{
          position: 'absolute', inset: '-4px', borderRadius: '20px',
          background: 'linear-gradient(135deg,#1B61AC,#00A8DB)',
          filter: 'blur(14px)', opacity: 0.55,
        }} />
        <div style={{
          position: 'relative', width: '68px', height: '68px', borderRadius: '18px',
          background: 'linear-gradient(135deg,#1B61AC,#00A8DB)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 0 1.5px rgba(139,92,246,0.35),0 10px 40px rgba(139,92,246,0.25)',
          margin: '0 auto',
        }}>
          <DWILogoIcon size={60} />
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 900, letterSpacing: '-0.5px', lineHeight: 1.1, marginBottom: '5px' }}>
        <span style={{ color: '#F0F6FF' }}>DWI </span>
        <span style={{
          background: 'linear-gradient(135deg,#1B61AC,#00A8DB)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>FINTECH</span>
      </div>
      <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(232,239,248,0.28)' }}>
        {t(lang, 'signup.tagline')}
      </div>
    </div>
  );
}

function Backgrounds() {
  return (
    <>
      <div aria-hidden style={{
        position: 'absolute', top: '-120px', left: '50%', transform: 'translateX(-50%)',
        width: '600px', height: '600px', borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle,rgba(139,92,246,0.12) 0%,transparent 65%)',
      }} />
      <div aria-hidden style={{
        position: 'absolute', bottom: '-100px', right: '-60px',
        width: '400px', height: '400px', borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle,rgba(6,200,232,0.09) 0%,transparent 65%)',
      }} />
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(6,200,232,0.07) 1px,transparent 1px)',
        backgroundSize: '28px 28px',
      }} />
    </>
  );
}

// ── Register ───────────────────────────────────────────────────────────────
export default function Register() {
  const { register } = useAuth();
  const navigate     = useNavigate();
  const [params]     = useSearchParams();
  const { countries, selectCountry } = useCountry();
  const [lang, setLang]             = useState(() => (params.get('lang') === 'fr' ? 'fr' : 'en'));
  const [langPicked, setLangPicked] = useState(params.get('lang') === 'fr');
  const T = (k, v) => t(lang, k, v);
  const pickLang = l => { setLang(l); setLangPicked(true); };
  // Known server messages, shown in French when French is on.
  const serverMsg = m => ({ 'Email or phone already registered': T('signup.err.exists'),
    'Sign-up is not open in that country yet': T('signup.err.closed') }[m] || m);

  const [step, setStep]             = useState(1);
  const [selectedCode, setSelectedCode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);

  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '',
    referralCode: params.get('ref') || '',
    country: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [showPass, setShowPass] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleCountrySelect = (code) => {
    setSelectedCode(code);
  };

  const handleCountryContinue = async () => {
    if (!selectedCode) { setError(T('signup.country.required')); return; }
    setError('');
    if (selectedCode === 'CD' && !langPicked) setLang('fr');
    const c = await selectCountry(selectedCode);
    setSelectedCountry(c);
    setForm(f => ({ ...f, country: c ? c.name : selectedCode }));
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError(T('signup.password.short')); return; }
    setLoading(true);
    try {
      // The language goes with sign-ups outside Uganda only (Uganda's sign-up is unchanged).
      await register(selectedCode && selectedCode !== 'UG' ? { ...form, language: lang } : form);
      navigate(safeNext(params.get('next')));
    } catch (err) {
      setError(serverMsg(err.message) || T('signup.failed'));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 1: Country selection ─────────────────────────────────────────
  if (step === 1) {
    return (
      <div style={pageWrap}>
        <Backgrounds />
        <div style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 10 }}>
          <Brand lang={lang} />
          {params.get('lang') && <LangToggle lang={lang} onChange={pickLang} />}
          <div style={cardOuter}>
            <div style={topBar} />
            <div style={{ padding: '24px 26px 28px' }}>
              <div style={{ marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#F0F6FF', margin: 0 }}>
                  {T('signup.country.title')}
                </h2>
                <p style={{ color: 'rgba(232,239,248,0.4)', fontSize: '13px', marginTop: '3px', marginBottom: 0 }}>
                  {T('signup.country.sub')}
                </p>
              </div>

              {error && (
                <div style={{
                  marginBottom: '16px', padding: '11px 14px', borderRadius: '12px',
                  background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
                  color: '#f87171', fontSize: '13px',
                }}>{error}</div>
              )}

              <CountrySelector onSelect={handleCountrySelect} selectedCode={selectedCode} />

              <button
                type="button"
                onClick={handleCountryContinue}
                disabled={!selectedCode}
                style={{
                  width: '100%', marginTop: '16px', padding: '14px 0',
                  borderRadius: '12px', border: 'none',
                  cursor: selectedCode ? 'pointer' : 'not-allowed',
                  fontWeight: 800, fontSize: '15px', color: '#0A1628',
                  background: selectedCode
                    ? 'linear-gradient(135deg,#1B61AC 0%,#00A8DB 100%)'
                    : 'rgba(139,92,246,0.25)',
                  boxShadow: selectedCode ? '0 6px 28px rgba(139,92,246,0.38)' : 'none',
                  opacity: selectedCode ? 1 : 0.6,
                  transition: 'all 0.2s',
                  boxSizing: 'border-box',
                }}
              >
                {T('signup.continue')}
              </button>

              <p style={{ textAlign: 'center', fontSize: '14px', color: 'rgba(232,239,248,0.4)', marginTop: '14px', marginBottom: 0 }}>
                {T('signup.have_account')}{' '}
                <Link to={withNext('/login', params.get('next'))}
                  style={{ color: '#00A8DB', fontWeight: 700, textDecoration: 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                  onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}>
                  {T('signup.sign_in')}
                </Link>
              </p>
            </div>
          </div>
          <p style={{ textAlign: 'center', fontSize: '11px', color: 'rgba(232,239,248,0.16)', marginTop: '18px' }}>
            © 2026 DWI Fintech · Secure Digital Finance Ecosystem
          </p>
        </div>
      </div>
    );
  }

  // ── Step 2: Registration form ─────────────────────────────────────────
  return (
    <div style={pageWrap}>
      <Backgrounds />
      <div style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 10 }}>
        <Brand lang={lang} />
        {(params.get('lang') || (selectedCode && selectedCode !== 'UG')) && <LangToggle lang={lang} onChange={pickLang} />}
        <div style={cardOuter}>
          <div style={topBar} />
          <div style={{ padding: '24px 26px 28px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#F0F6FF', margin: 0 }}>{T('signup.title')}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                <Globe size={13} color="#00A8DB" />
                <span style={{ color: 'rgba(232,239,248,0.55)', fontSize: '13px' }}>
                  {selectedCountry ? `${selectedCountry.flag || ''} ${selectedCountry.name}` : form.country}
                </span>
                <button
                  type="button"
                  onClick={() => { setStep(1); setError(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#00A8DB', fontSize: '12px', padding: 0, fontWeight: 700 }}>
                  {T('signup.change')}
                </button>
              </div>
            </div>

            {error && (
              <div style={{
                marginBottom: '16px', padding: '11px 14px', borderRadius: '12px',
                background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
                color: '#f87171', fontSize: '13px',
              }}>{error}</div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Field icon={User}  label={T('signup.name')}  type="text"  placeholder="John Mbula"      value={form.name}  onChange={set('name')} />
              <Field icon={Mail}  label={T('signup.email')} type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} />
              <Field icon={Phone} label={T('signup.phone')} type="tel"
                placeholder={selectedCode === 'CD' ? '+243 8X XXX XXXX' : '+256 77X XXX XXX'} value={form.phone} onChange={set('phone')} />
              <Field icon={Lock}  label={T('signup.password')} type={showPass ? 'text' : 'password'}
                placeholder={T('signup.password.ph')} value={form.password} onChange={set('password')}
                rightEl={
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(232,239,248,0.35)', padding: 0, display: 'flex' }}>
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
              />
              {form.referralCode && (
                <Field icon={Gift} label={T('signup.referral')} type="text" placeholder="DWIXXXXXX"
                  value={form.referralCode} onChange={set('referralCode')}
                  note={T('signup.referral.note')} />
              )}

              <button type="submit" disabled={loading}
                style={{
                  width: '100%', marginTop: '6px', padding: '14px 0',
                  borderRadius: '12px', border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 800, fontSize: '15px', color: '#0A1628',
                  background: loading
                    ? 'rgba(139,92,246,0.45)'
                    : 'linear-gradient(135deg,#1B61AC 0%,#00A8DB 100%)',
                  boxShadow: loading ? 'none' : '0 6px 28px rgba(139,92,246,0.38)',
                  opacity: loading ? 0.75 : 1,
                  transition: 'box-shadow 0.2s,transform 0.15s,opacity 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  boxSizing: 'border-box',
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.boxShadow = '0 8px 36px rgba(139,92,246,0.52)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 6px 28px rgba(139,92,246,0.38)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                {loading ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      style={{ animation: 'dwi-spin 0.8s linear infinite' }}>
                      <circle cx="12" cy="12" r="10" stroke="rgba(7,11,20,0.35)" strokeWidth="3.5" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="#0A1628" strokeWidth="3.5" strokeLinecap="round" />
                    </svg>
                    {T('signup.submitting')}
                  </>
                ) : T('signup.submit')}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: '11px', color: 'rgba(232,239,248,0.28)', marginTop: '16px', lineHeight: 1.5, marginBottom: 0 }}>
              {T('signup.terms')}
            </p>

            <p style={{ textAlign: 'center', fontSize: '14px', color: 'rgba(232,239,248,0.4)', marginTop: '14px', marginBottom: 0 }}>
              {T('signup.have_account')}{' '}
              <Link to={withNext('/login', params.get('next'))}
                style={{ color: '#00A8DB', fontWeight: 700, textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}>
                {T('signup.sign_in')}
              </Link>
            </p>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: '11px', color: 'rgba(232,239,248,0.16)', marginTop: '18px' }}>
          © 2026 DWI Fintech · Secure Digital Finance Ecosystem
        </p>
      </div>
    </div>
  );
}